import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemGrid } from '../components/ui/modules/ItemGridModule';
import { TextBlock } from '../components/ui/modules/TextBlocksModule';
import {
  ALTERNATIVE_FUNDS_PREFIX_URL,
  INSIGHT_PREFIX_URL,
  LEGAL_DOCS_PREFIX_URL,
  MUTUAL_PREFIX_URL,
} from '../constants';
import { AreaOfExpertise } from '../templates/AreaOfExpertisePage';
import { Service } from '../templates/ServicePage';
import { urlJoin } from './utils';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget(options: { url: string }): void;
      showPopupWidget(url: string): void;
    };
  }
}

export function handleCalendlyClick(link: string) {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: link });
  }
}

export function getMenuLinkClasses(
  currentPathname: string,
  linkUrl: string,
  selectedClass: string,
): string {
  let classes = '';

  let currentPathnameCleaned = currentPathname.trim();
  let linkUrlCleaned = linkUrl.trim();
  if (currentPathnameCleaned.endsWith('/')) {
    currentPathnameCleaned = currentPathnameCleaned.substr(0, currentPathnameCleaned.length - 1);
  }

  if (linkUrlCleaned.endsWith('/')) {
    linkUrlCleaned = linkUrlCleaned.substr(0, linkUrlCleaned.length - 1);
  }
  if (currentPathnameCleaned === linkUrlCleaned) {
    classes += ' ' + selectedClass;
  }
  return classes;
}

export function formatBytes(bytes: number, decimalPlaces = 2): string {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const log1024 = Math.floor(Math.log(bytes) / Math.log(1024));
  const unit = ['b', 'K', 'M', 'G', 'T', 'P'][log1024];
  if (!unit) {
    throw new Error('Could not find a unit for given bytes number: ' + bytes);
  }
  // Using parseFloat to remove trailing zeros
  return parseFloat((bytes / Math.pow(1024, log1024)).toFixed(decimalPlaces)) + unit;
}

export function getReferenceUrl(
  typeName:
    | 'SanityInsight'
    | 'SanityAlternativeFund'
    | 'SanitySecurityFund'
    | 'SanityPage'
    | 'SanityLegalDocumentation'
    | 'SanityInsightsPage',
  slug: string,
): string {
  switch (typeName) {
    case 'SanityInsight':
      return urlJoin(INSIGHT_PREFIX_URL, slug);
    case 'SanityAlternativeFund':
      return urlJoin(ALTERNATIVE_FUNDS_PREFIX_URL, slug);
    case 'SanitySecurityFund':
      return urlJoin(MUTUAL_PREFIX_URL, slug);
    case 'SanityPage':
      return urlJoin(slug);
    case 'SanityLegalDocumentation':
      return urlJoin(LEGAL_DOCS_PREFIX_URL, slug);
    case 'SanityInsightsPage':
      return urlJoin(INSIGHT_PREFIX_URL);
    default:
      throw new Error('typeName value not known: ' + typeName);
  }
}

export function useRunOnceWhenVisible(ref, callback) {
  const hasRanBefore = useRef(false);

  useEffect(() => {
    if (ref) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !hasRanBefore.current) {
          hasRanBefore.current = true;
          callback();
        }
      });

      observer.observe(ref);
      return () => {
        observer.disconnect();
      };
    }
    return undefined;
  }, [ref]);
}

export function getAreasOfExpertiseAsBlocks(
  areasOfExpertise: Array<AreaOfExpertise>,
): Array<ItemGrid | TextBlock> {
  const { t } = useTranslation();

  const areasOfExpertiseAsBlocks = areasOfExpertise.map(areaOfExpertise => ({
    title: areaOfExpertise.title,
    text: areaOfExpertise.description || areaOfExpertise.hero.text,
    link: {
      text: t('knowMoreButton.title', 'Saiba mais'),
      linkType: 'internalLink',
      internalLink: {
        pageReference: {
          __typename: 'SanityAreaOfExpertise',
          slug: areaOfExpertise.slug,
        },
      },
    },
  })) as Array<ItemGrid | TextBlock>;
  return areasOfExpertiseAsBlocks;
}

export function getServicesAsBlocks(services: Array<Service>): Array<ItemGrid | TextBlock> {
  const { t } = useTranslation();

  const servicesAsBlocks = services.map(service => ({
    title: service.title,
    text: service.description || service.hero.text,
    link: {
      text: t('knowMoreButton.title', 'Saiba mais'),
      linkType: 'internalLink',
      internalLink: {
        pageReference: {
          __typename: 'SanityService',
          slug: service.slug,
        },
      },
    },
    icon: service.icon,
  })) as Array<ItemGrid | TextBlock>;
  return servicesAsBlocks;
}

export function formatDate(isoString: string, locale: string = 'en') {
  const date = new Date(isoString);

  const monthNames = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    pt: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
  };

  const day = date.getDate();
  const month = monthNames[locale][date.getMonth()];
  const year = date.getFullYear();

  const suffix = getDaySuffix(day, locale);

  if (locale === 'pt') {
    return `${day} ${month} ${year}`;
  } else {
    return `${month} ${day}${suffix} ${year}`;
  }
}

function getDaySuffix(day: number, locale: string = 'en') {
  if (locale === 'pt') return '';

  if (day > 3 && day < 21) return 'th';

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
